import * as React from 'react'
import { connect } from 'react-redux'
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next'
import { withRouter, RouteComponentProps } from 'react-router'
import MultiFileModal from './MultiFileModal'
import DeleteFileModal from './DeleteFileModal'
import Column from '@mv-submodules/inplant-components-fe/ui/components/Grid/Column'
import { Button } from '@mv-submodules/inplant-components-fe'
import IconComponent from '../../../../../inplant-components-fe/ui/components/MVIcon/Icon'
import { showNotification } from '@mv-submodules/mvlabs-components-fe/ui/components/Notification/Notification'
import FormComponentBuilder from '../FormComponent/FormComponentBuilder'
import {
  checklistMultiFileComponentUploads,
  checklistMultiFileGetUploaded,
} from '../../../../redux/actions/checklistEdit'
import { ChecklistComponentFileData, EditComponentMetadataError } from '../../../../redux/reducers/checklistEdit'
import { useState } from 'react'
import { PendingUploads, SavedBadge, useObjectUrl } from '../UploadTile/UploadTile'
import { usePendingUploads } from '../../../../hook/useEditChecklist'

export interface FileData {
  path: string
  blob?: Blob
}

export interface ComponentData {
  id: string
  optional: boolean
  type: {
    type: string
    values: string[]
  }
  defaultValue: Array<{ file: string }>
  lastValue: Array<{ file: string }>
}

export interface StateProps {
  componentData: ComponentData
  checklistInstanceId: string
  checkId: string
}

export interface DispatchProps {
  getFiles: (componentId: string, options?: { preview: boolean }) => Promise<Blob>
  updateFiles: (componentId: string, checkId: string, files: File[]) => void
}

export interface OwnStateProps {
  fileModalShow: boolean
  fileModalPath: string | undefined
  deleteFileModalShow: boolean
  deleteFileModalFilePath: string | undefined
}

export interface OwnProps extends RouteComponentProps<any> {
  disabled?: boolean
  collapseHandler?: Function
}

export type Props = StateProps & DispatchProps & OwnProps & WithTranslation

const mapStateToProps = (state: any) => ({})

const mapDispatchToProps = (dispatch: Function): DispatchProps => {
  return {
    getFiles: (componentId, options) => dispatch(checklistMultiFileGetUploaded(componentId, options)),
    updateFiles: (componentId, checkId, files) =>
      dispatch(checklistMultiFileComponentUploads(componentId, checkId, files)),
  }
}

export class MultiFileComponent extends React.Component<Props, OwnStateProps> {
  constructor(props: Props) {
    super(props)
    this.state = {
      fileModalShow: false,
      fileModalPath: undefined,
      deleteFileModalShow: false,
      deleteFileModalFilePath: undefined,
    }
    this.updateFiles = this.updateFiles.bind(this)
    this.handleFileModalClose = this.handleFileModalClose.bind(this)
    this.handleFileModalOpen = this.handleFileModalOpen.bind(this)
    this.handleDeleteFileModalClose = this.handleDeleteFileModalClose.bind(this)
    this.handleDeleteFileModalOpen = this.handleDeleteFileModalOpen.bind(this)
    this.manageCollapsable()
    this.handleError = this.handleError.bind(this)
  }

  public componentDidMount() {
    if (this.props.componentData.lastValue.length > 0) {
      this.props.getFiles(this.props.componentData.id, { preview: true })
    }
  }

  private manageCollapsable() {
    if (
      this.props.componentData.lastValue.length > 0 &&
      this.props.componentData.optional &&
      this.props.collapseHandler
    ) {
      this.props.collapseHandler()
    }
  }

  private async updateFiles(event: React.ChangeEvent<HTMLInputElement>) {
    event.persist()
    const files = event.target.files
    if (!files) {
      return
    }

    this.props.updateFiles(this.props.componentData.id, this.props.checkId, Array.from(files))
    // allow to select the same file again (e.g. after a failed upload)
    event.target.value = ''
  }

  private handleFileModalOpen(filePath: string) {
    this.setState(prevState => ({ ...prevState, fileModalShow: true, fileModalPath: filePath }))
  }

  private handleFileModalClose() {
    this.setState(prevState => ({ ...prevState, fileModalShow: false }))
  }

  private handleDeleteFileModalOpen(filePath: string) {
    this.setState(prevState => ({ ...prevState, deleteFileModalShow: true, deleteFileModalFilePath: filePath }))
  }

  private handleDeleteFileModalClose() {
    this.setState(prevState => ({
      ...prevState,
      deleteFileModalShow: false,
      deleteFileModalFilePath: undefined,
    }))
  }

  private handleError(error: EditComponentMetadataError) {
    if (!error || error.type !== 'server') {
      return
    }

    if (error.message?.includes('Invalid Exif')) {
      showNotification({ type: 'danger', message: this.props.t('checklist.edit.errors.invalidExif') })
    }
  }

  public render() {
    return (
      <div className="multi-file-component">
        <FormComponentBuilder<ChecklistComponentFileData[]>
          component={this.props.componentData}
          onErrorReceived={this.handleError}
        >
          {({ className, updating, value: files, disabled, onBlur }) => {
            return (
              <>
                <MultiFileModal
                  show={this.state.fileModalShow}
                  onClose={this.handleFileModalClose}
                  filePath={this.state.fileModalPath}
                />
                <DeleteFileModal
                  componentId={this.props.componentData.id}
                  show={this.state.deleteFileModalShow}
                  onClose={this.handleDeleteFileModalClose}
                  filePath={this.state.deleteFileModalFilePath}
                />
                {/* while loading the input is disabled and each thumbnail shows its own spinner */}
                <div className={`custom-file ${className} ${this.props.disabled ? 'hidden' : ''}`}>
                  <input
                    type={'file'}
                    multiple={true}
                    className={'custom-file-input'}
                    id={`${this.props.componentData.id}-multi-file`}
                    lang={'it'}
                    onChange={this.updateFiles}
                    disabled={disabled}
                    onBlur={onBlur}
                  />
                  <label className="custom-file-label" htmlFor="customFileLang">
                    {this.props.t('checklist.components.browse')}
                  </label>
                </div>
                <_Thumbnails
                  componentId={this.props.componentData.id}
                  files={files}
                  loading={updating}
                  disabled={disabled}
                  onClick={this.handleFileModalOpen}
                  onClickDelete={this.handleDeleteFileModalOpen}
                />
              </>
            )
          }}
        </FormComponentBuilder>
      </div>
    )
  }
}

function _Spinner() {
  return (
    <div className="d-flex justify-content-center">
      <IconComponent icon={'circle-notch'} className="spinner" spin={true} size="1x" />
    </div>
  )
}

/**
 * The saved photos and the ones being uploaded.
 */
function _Thumbnails({
  componentId,
  files,
  loading,
  disabled,
  onClick,
  onClickDelete,
}: {
  componentId: string
  files: FileData[]
  loading: boolean
  disabled: boolean
  onClick: (filePath: string) => void
  onClickDelete: (filePath: string) => void
}) {
  const uploads = usePendingUploads(componentId)
  // the saved photos can't be deleted until the uploads of the component are concluded
  const uploadsInProgress = uploads.some(upload => upload.phase !== 'failed')

  if (files.length === 0 && uploads.length === 0) {
    return null
  }

  return (
    <div className="multi-file-thumbs">
      {files.map(file => (
        <_Image
          key={file.path}
          file={file}
          loading={loading}
          disabled={disabled || uploadsInProgress}
          onClick={() => onClick(file.path)}
          onClickDelete={() => onClickDelete(file.path)}
        />
      ))}
      <PendingUploads componentId={componentId} />
    </div>
  )
}

function _Image({
  disabled,
  file,
  loading,
  onClick,
  onClickDelete,
}: {
  file: FileData
  loading: boolean
  onClick: () => void
  disabled: boolean
  onClickDelete: () => void
}) {
  const [error, setError] = useState(false)

  if (!file.blob && loading) {
    return (
      <div className="multi-file-thumb">
        <div className="multi-file-thumb-loading">
          <_Spinner />
        </div>
      </div>
    )
  }

  // the preview is missing when it could not be downloaded
  if (error || !file.blob) {
    return (
      <div className="multi-file-thumb">
        {!disabled ? <Button variant={'secondary'} onClick={onClickDelete} icon={'times'} /> : null}
        <_PreviewError />
        <SavedBadge />
      </div>
    )
  }

  return (
    <div className="multi-file-thumb">
      {!disabled ? <Button variant={'secondary'} onClick={onClickDelete} icon={'times'} /> : null}
      <_PreviewImage blob={file.blob} onClick={onClick} onError={() => setError(true)} />
      <SavedBadge />
    </div>
  )
}

function _PreviewError() {
  const { t } = useTranslation()
  return (
    <div>
      <div className="d-flex align-items-center justify-content-around border border-black">
        <Column xs={1}>
          <IconComponent icon={'exclamation-triangle'} />
        </Column>
        <Column xs={8}>
          <div className="text-left align-items-center">{t('checklist.components.previewImageError')}</div>
        </Column>
      </div>
    </div>
  )
}

function _PreviewImage({ blob, onClick, onError }: { blob: Blob; onClick: () => void; onError: () => void }) {
  const src = useObjectUrl(blob)

  return (
    <img
      className="img-fluid mx-auto d-block img-thumbnail"
      src={src}
      onClick={onClick}
      onError={onError}
    />
  )
}

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(MultiFileComponent)))
